import { Button, Menu, MenuButton, MenuItem, MenuList } from "@chakra-ui/react";
import React from "react";
import { RiArrowDropDownLine } from "react-icons/ri";
import { useSearchParams } from "react-router-dom";
import {
  AttendanceTypes,
  ClientStorageTypes,
  SearchAndFilterTypes,
} from "src/constants";
import { useSchoolAdmin } from "src/hooks/useSchoolAdmin";

export const useAttendance = () => {
  const schoolAdmin = useSchoolAdmin();
  const { isElevatedSchoolAdmin, hasApplyingSchools } = schoolAdmin;
  const [searchParams, setSearchParams] = useSearchParams();

  const defaultAttendance = hasApplyingSchools
    ? AttendanceTypes.Applying
    : AttendanceTypes.Attending;

  const attendance =
    (localStorage.getItem(ClientStorageTypes.Attendance) as
      | AttendanceTypes
      | undefined) ??
    (searchParams.get(SearchAndFilterTypes.Attendance) as
      | AttendanceTypes
      | undefined) ??
    defaultAttendance;

  // Resolve attendance from (in priority order):
  // 1. Query string, 2. Local storage, 3. Default based on hasApplyingSchools.
  // Then sync the resolved value back to both stores.
  React.useEffect(() => {
    if (!isElevatedSchoolAdmin) return;

    const urlAttendance = searchParams.get(SearchAndFilterTypes.Attendance);
    const localAttendance = localStorage.getItem(ClientStorageTypes.Attendance);

    let resolvedAttendance = urlAttendance ?? localAttendance;

    const validValues: string[] = Object.values(AttendanceTypes);
    if (!resolvedAttendance || !validValues.includes(resolvedAttendance)) {
      resolvedAttendance = defaultAttendance;
    }

    // Sync local storage
    localStorage.setItem(ClientStorageTypes.Attendance, resolvedAttendance);

    if (
      searchParams.get(SearchAndFilterTypes.Attendance) !== resolvedAttendance
    ) {
      searchParams.set(SearchAndFilterTypes.Attendance, resolvedAttendance);
      setSearchParams(searchParams, { replace: true });
    }
  }, [isElevatedSchoolAdmin, defaultAttendance, searchParams, setSearchParams]);

  const setAttendanceCallback = React.useCallback(
    (value: string) => {
      switch (value) {
        case AttendanceTypes.Applying: {
          searchParams.set(
            SearchAndFilterTypes.Attendance,
            AttendanceTypes.Applying
          );
          localStorage.setItem(
            ClientStorageTypes.Attendance,
            AttendanceTypes.Applying
          );
          break;
        }
        case AttendanceTypes.Attending: {
          searchParams.set(
            SearchAndFilterTypes.Attendance,
            AttendanceTypes.Attending
          );
          localStorage.setItem(
            ClientStorageTypes.Attendance,
            AttendanceTypes.Attending
          );
          break;
        }
        default: {
          searchParams.delete(SearchAndFilterTypes.Attendance);
          localStorage.removeItem(ClientStorageTypes.Attendance);
          break;
        }
      }

      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  if (!isElevatedSchoolAdmin) {
    return { attendance: undefined, selector: null };
  }

  return {
    attendance,
    attendanceSelector: (
      <AttendanceSelector
        attendance={attendance}
        setAttendance={setAttendanceCallback}
      />
    ),
  };
};

export const AttendanceSelector: React.FC<{
  attendance: AttendanceTypes;
  setAttendance: (data: string) => void;
}> = ({ attendance, setAttendance }) => {
  const schoolAdmin = useSchoolAdmin();
  const { isElevatedSchoolAdmin, hasApplyingSchools, hasAttendingSchools } =
    schoolAdmin;
  const options = [
    ...(hasApplyingSchools ? [AttendanceTypes.Applying] : []),
    ...(hasAttendingSchools ? [AttendanceTypes.Attending] : []),
  ];
  const isDisabled = options.length < 2;

  if (!isElevatedSchoolAdmin || !options.length) return null;

  return (
    <Menu>
      <MenuButton
        as={Button}
        rightIcon={<RiArrowDropDownLine />}
        size="sm"
        variant="banner"
        isDisabled={isDisabled}
      >
        {labels[attendance] ?? "Select student filter"}
      </MenuButton>
      <MenuList>
        {options.map((opt) => (
          <MenuItem key={opt} value={opt} onClick={() => setAttendance(opt)}>
            {labels[opt]}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
};

const labels = {
  [AttendanceTypes.Applying]: "Applying students",
  [AttendanceTypes.Attending]: "Current students",
} as const;
